import {storageEngine} from './storageEngine'

/**
 * Interface for handling settings and properties stored in localStorage
 * @class
 */
class Storage {

    /** @hideconstructor */
    constructor(parentNamespace = '__settings__') {
        this.parentNamespace = parentNamespace
        this.storage = storageEngine()
    }

    /**
     * Check that local storage is available before any operation is performed
     *
     * @protected
     *
     * @returns {boolean}
     */
    isAvailable() {
        return this.storage !== false
    }

    /**
     * Retrieve all settings stored. Returns false on failure, object on success.
     *
     * @returns {*}
     */
    getAllSettings(namespace) {
        if (!this.isAvailable()) {
            return null
        }

        const regex = new RegExp('^' + this.parentNamespace + namespace + '.(.*)')
        let settings = {}
        Object.keys(this.storage).forEach((key) => {
            const found = key.match(regex)
            if (Array.isArray(found) && found.length === 2) {
                settings[found[1]] = this.get(namespace, found[1])
            }
        })

        return settings
    }

    /**
     * Get a value for a specific setting. Return null, or default value when specified
     * when not existing.
     *
     * @param {String}  namespace
     * @param {*}       defaultValue
     * @param {string}  key
     * @returns {*}
     */
    get(namespace, key, defaultValue = null) {
        if (!this.isAvailable()) {
            return null
        }

        const value = this.storage.getItem(`${this.parentNamespace}${namespace}.${key}`)
        return (!value) ? defaultValue : JSON.parse(value)
    }

    /**
     * Set a value for a specific setting, transformed into JSON. Returns
     * true on success, false on failure.
     *
     * @param {string} namespace
     * @param {string} key
     * @param {*} value
     *
     * @returns {boolean}
     */
    set(namespace, key, value) {
        if (!this.isAvailable()) {
            return false
        }

        try {
            const jsonValue = JSON.stringify(value)
            this.storage.setItem(`${this.parentNamespace}${namespace}.${key}`, jsonValue)
        }
        catch(err) {
            console.error(err)
            return false
        }

        return true
    }

    /**
     * Delete a specific setting. Returns true on success, false on failure.
     *
     * @param {string} namespace
     * @param {string} key
     * @returns {boolean}
     */
    delete(namespace, key) {
        if (!this.isAvailable()) {
            return false
        }

        this.storage.removeItem(`${this.parentNamespace}${namespace}.${key}`)

        return true
    }
}

export {Storage}
